import { useMemo, useState, type JSX } from 'react';
import { ChevronDown, X } from 'lucide-react';

import { useTenantSettingsQuery } from '../../../tenants/site-status-card/use-tenant-settings.js';

/** Minute step of the hour list (Piotr's note II.1). */
export type HourStep = 30 | 15;

/** Hour range mode (Piotr's note II.1): `12h` = site working hours, `24h` = the full day. */
export type HourRange = '12h' | '24h';

/** Working window of the site (D4a), as `HH:MM`; `null` bounds mean no narrowing. */
export interface SiteWorkingHours {
  from: string | null;
  to: string | null;
}

const FULL_RANGE_FROM = '00:00';
const FULL_RANGE_TO = '23:45';

function minutesOf(hhmm: string): number {
  const [hours, minutes] = hhmm.split(':');
  return Number(hours) * 60 + Number(minutes);
}

function hhmmOf(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const minutesPart = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutesPart).padStart(2, '0')}`;
}

/**
 * Slots of the hour list (D4b — Piotr's model, feature visit-cockpit/visit-hour-picker).
 *
 * The window is either the site's declared working hours (`12h`) or the whole day
 * 00:00–23:45 (`24h`). The extreme bounds are SNAPPED to the step grid (a step laid out
 * from midnight), so a window like 08:15–16:45 with step 30 yields 08:30–16:30; a site with
 * no declared window falls back to the full range (D4a: "brak wartości = pełny zakres").
 */
export function buildHourSlots(
  step: HourStep,
  range: HourRange,
  siteWindow: SiteWorkingHours | null,
): string[] {
  const from = range === '24h' ? FULL_RANGE_FROM : (siteWindow?.from ?? FULL_RANGE_FROM);
  const to = range === '24h' ? FULL_RANGE_TO : (siteWindow?.to ?? FULL_RANGE_TO);
  const first = Math.ceil(minutesOf(from) / step) * step;
  const last = Math.floor(minutesOf(to) / step) * step;
  const slots: string[] = [];
  for (let minute = first; minute <= last; minute += step) {
    slots.push(hhmmOf(minute));
  }
  return slots;
}

export interface VisitHourPickerProps {
  /** Selected hour (`HH:MM`) or `''` when none is chosen. */
  value: string;
  /** Called with the picked `HH:MM` slot, or `''` when the hour is cleared. */
  onChange: (value: string) => void;
  disabled?: boolean;
}

const fieldClass =
  'rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 disabled:opacity-50';
const toggleClass = `${fieldClass} tabular-nums`;
const labelClass = 'text-xs text-slate-400';

/**
 * D4b/c — the hour picker of the planning dialog: a slot list whose minute step (`30`/`15`)
 * and hour range (`12h` = site operating hours / `24h` = full day) are toggled from this row.
 * Both toggles live in the DIALOG's state (session-only, never persisted) and a fresh dialog
 * starts at the defaults 30 and 12h. The `X` clears only the chosen hour and is shown only
 * when there is one; the site's working hours come from D4a (`tenant/settings`).
 */
export function VisitHourPicker({
  value,
  onChange,
  disabled = false,
}: VisitHourPickerProps): JSX.Element {
  const settingsQuery = useTenantSettingsQuery();
  const [step, setStep] = useState<HourStep>(30);
  const [range, setRange] = useState<HourRange>('12h');
  const [open, setOpen] = useState(false);

  const windowFrom = settingsQuery.data?.workingHoursFrom ?? null;
  const windowTo = settingsQuery.data?.workingHoursTo ?? null;
  const slots = useMemo(
    () => buildHourSlots(step, range, { from: windowFrom, to: windowTo }),
    [step, range, windowFrom, windowTo],
  );

  return (
    <div>
      <span id="schedule-visit-time-label" className={labelClass}>
        Godzina umówienia (opcjonalnie)
      </span>
      <div className="mt-1 flex items-center gap-1">
        <button
          type="button"
          aria-labelledby="schedule-visit-time-label"
          aria-haspopup="true"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          className={`${fieldClass} flex flex-1 items-center justify-between`}
        >
          <span className={value === '' ? 'text-slate-500' : ''}>
            {value === '' ? 'Wybierz godzinę' : value}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </button>
        {value !== '' ? (
          <button
            type="button"
            aria-label="Wyczyść godzinę"
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onChange('');
            }}
            className={fieldClass}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Krok minut"
          title="Krok minut"
          disabled={disabled}
          onClick={() => setStep((current) => (current === 30 ? 15 : 30))}
          className={toggleClass}
        >
          {step}
        </button>
        <button
          type="button"
          aria-label="Zakres godzin"
          title={range === '12h' ? 'Godziny pracy ośrodka' : 'Pełny zakres doby'}
          disabled={disabled}
          onClick={() => setRange((current) => (current === '12h' ? '24h' : '12h'))}
          className={toggleClass}
        >
          {range}
        </button>
      </div>
      {open ? (
        <ul
          aria-labelledby="schedule-visit-time-label"
          className="mt-1 max-h-40 overflow-auto rounded-md border border-slate-700 bg-slate-950 py-1"
        >
          {slots.map((slot) => (
            <li key={slot}>
              <button
                type="button"
                onClick={() => {
                  onChange(slot);
                  setOpen(false);
                }}
                className={`block w-full px-3 py-1 text-left text-xs hover:bg-slate-800 ${
                  slot === value ? 'bg-slate-800 text-cyan-300' : 'text-slate-200'
                }`}
              >
                {slot}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}